import { useOrganizationAreAiTermsOfUseAccepted } from '@monorepo/settings/src/hooks/useOrganizationAreAiTermsOfUseAccepted';
import { Loading } from '@monorepo/shared/components/Loading';
import { bodySmall, header3, header4 } from '@monorepo/shared/styles/text';
import DiamondIcon from '@svg/diamond.svg';
import LandscapeImageIcon from '@svg/landscape-image.svg';
import TableIcon from '@svg/table.svg';
import { EDP_AI_SUPPORTED_FILE_TYPES_LABELS } from 'mapistry-shared';
import { useFeatureFlags } from 'mapistry-shared/api';
import React from 'react';
import styled from 'styled-components';
import { Button } from '../../Button/Button';
import { BaseModal } from '../../modals/BaseModal';

export const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 16rem;
`;

export const SubContainer = styled.div`
  margin: 4rem;
`;

const Title = styled.div`
  ${header3}
  text-align: center;
`;

const Container = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 16px;
`;

const StyledButton = styled(Button)`
  width: 12rem;
  height: 7rem;

  svg {
    width: 1.25em;
    height: 1em;
  }

  & .MuiButton-label {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: flex-start;
  }
`;

const ButtonTitle = styled.div`
  ${header4}
  color: ${({ theme }) => theme.colors.black};
`;

const ButtonDescription = styled.div`
  ${bodySmall}
`;

export enum UploadType {
  EDP_AI_UPLOADER = 'edpAiUploader',
  FLATFILE_CSV_UPLOADER = 'flatfileCsvUploader',
}

const UpsellContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 12rem;
  height: 7rem;
  padding: 14px;
  text-align: left;
  background: ${({ theme }) => theme.colors.blue};
  border-radius: 0.25rem;
`;

const DiamondIconContainer = styled.div`
  display: flex;
  align-items: center;
  width: 1.75rem;
  height: 1.75rem;
  background: ${({ theme }) => theme.colors.yellow};
  border-radius: 8px;

  & svg {
    width: 1.5em;
    height: 1em;
    margin: 4px 2px 2px;
  }
`;

const StyledDiamondIcon = styled(DiamondIcon)`
  fill: ${({ theme }) => theme.colors.blue};
`;

const UpsellTitleContainer = styled.div`
  ${header4}
  display: flex;
  color: ${({ theme }) => theme.colors.white};
`;

const UpsellDescription = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.white};

  & a,
  & a:hover {
    color: ${({ theme }) => theme.colors.white};
  }
`;

function ImageUploadOption({
  hasOrgPaidForEdpAiUploader,
  areAiTermsOfUseAccepted,
  setSelectedUploadType,
}: {
  hasOrgPaidForEdpAiUploader?: boolean;
  areAiTermsOfUseAccepted?: boolean;
  setSelectedUploadType: (uploadType: UploadType) => void;
}) {
  if (hasOrgPaidForEdpAiUploader && areAiTermsOfUseAccepted) {
    return (
      <StyledButton
        onClick={() => setSelectedUploadType(UploadType.EDP_AI_UPLOADER)}
        variant="outlined"
      >
        <LandscapeImageIcon aria-hidden />
        <ButtonTitle>Image</ButtonTitle>
        <ButtonDescription>
          {EDP_AI_SUPPORTED_FILE_TYPES_LABELS.join(', ')}
        </ButtonDescription>
      </StyledButton>
    );
  }

  if (hasOrgPaidForEdpAiUploader && !areAiTermsOfUseAccepted) {
    return (
      <UpsellContainer>
        <UpsellTitleContainer>
          <div>Image: Use AI to extract data</div>
          <DiamondIconContainer>
            <StyledDiamondIcon aria-hidden />
          </DiamondIconContainer>
        </UpsellTitleContainer>
        <UpsellDescription>
          An Organization Admin needs to agree to the{' '}
          <a href="/dashboard/settings/organization">AI Terms of Service</a>
        </UpsellDescription>
      </UpsellContainer>
    );
  }

  return (
    <UpsellContainer>
      <UpsellTitleContainer>
        <div>Image: Use AI to extract data</div>
        <DiamondIconContainer>
          <StyledDiamondIcon aria-hidden />
        </DiamondIconContainer>
      </UpsellTitleContainer>
      <UpsellDescription>
        Contact your customer success manager or{' '}
        <a href="mailto:help@mapistry.com">help@mapistry.com</a>
      </UpsellDescription>
    </UpsellContainer>
  );
}

export function LogEntriesSelectUploadTypeModal({
  organizationId,
  onClose,
  setSelectedUploadType,
}: {
  organizationId: string;
  onClose: () => void;
  setSelectedUploadType: (uploadType: UploadType) => void;
}) {
  const { hasOrgPaidForEdpAiUploader } = useFeatureFlags();
  const { areAiTermsOfUseAccepted, isLoading: isLoadingLogAiEnabled } =
    useOrganizationAreAiTermsOfUseAccepted(organizationId);

  return (
    <BaseModal
      onClose={onClose}
      open
      showCloseButton
      title="File Upload"
      buttons={null}
      maxWidth="xl"
      fullHeight
    >
      <ModalContent>
        {isLoadingLogAiEnabled ? (
          <Loading />
        ) : (
          <SubContainer>
            <Title>Which type of file do you want to upload?</Title>
            <Container>
              <StyledButton
                onClick={() =>
                  setSelectedUploadType(UploadType.FLATFILE_CSV_UPLOADER)
                }
                variant="outlined"
              >
                <TableIcon aria-hidden />
                <ButtonTitle>Spreadsheet</ButtonTitle>
                <ButtonDescription>CSV, XLSX, etc.</ButtonDescription>
              </StyledButton>
              <ImageUploadOption
                hasOrgPaidForEdpAiUploader={hasOrgPaidForEdpAiUploader}
                areAiTermsOfUseAccepted={areAiTermsOfUseAccepted}
                setSelectedUploadType={setSelectedUploadType}
              />
            </Container>
          </SubContainer>
        )}
      </ModalContent>
    </BaseModal>
  );
}
